import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@mono/ui-components';
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraIconButton, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraOverlayFooter } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { MoreHorizontal } from 'lucide-react';
import { useId, useState } from 'react';
import { AudienceHistoryDialog } from './AudienceHistoryDialog';
import { useRetractIntelClaim, useVerifyIntelClaim } from './hooks';
const COPY = {
    corroborate: {
        title: 'Corroborate',
        label: 'What backs it up',
        confirm: 'Mark corroborated'
    },
    contradict: {
        title: 'Mark contradicted',
        label: 'What contradicts it',
        confirm: 'Mark contradicted'
    },
    retract: {
        title: 'Retract',
        label: 'Why retract it',
        confirm: 'Retract'
    }
};
/**
 * Per-claim actions: Corroborate… / Mark contradicted… / Retract… for anyone
 * the server lets act; "Change who can see this…" for the author; "Audience
 * history" for everyone who sees the claim.
 */ export function ClaimActionsMenu({ claim, onChangeAudience }) {
    const [pending, setPending] = useState(null);
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const noteId = useId();
    const verify = useVerifyIntelClaim();
    const retract = useRetractIntelClaim();
    const close = ()=>{
        setPending(null);
        setNote('');
        setError('');
    };
    const confirm = ()=>{
        if (pending === 'retract') {
            if (!note.trim()) {
                setError('Say why you are retracting it');
                return;
            }
            retract.mutate({
                claimId: claim.id,
                reason: note.trim()
            }, {
                onSuccess: close
            });
            return;
        }
        if (pending === 'corroborate' || pending === 'contradict') {
            verify.mutate({
                claimId: claim.id,
                verification: pending === 'corroborate' ? 'CORROBORATED' : 'CONTRADICTED',
                note: note.trim(),
                artifactId: ''
            }, {
                onSuccess: close
            });
        }
    };
    const form = pending && pending !== 'history' ? COPY[pending] : null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(DropdownMenu, {
                children: [
                    /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsx(SpecraIconButton, {
                            label: "Claim actions",
                            variant: "ghost",
                            size: "sm",
                            "data-testid": Testids.registryHearsay.claimActions,
                            children: /*#__PURE__*/ _jsx(MoreHorizontal, {
                                className: "h-4 w-4",
                                "aria-hidden": true
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                        align: "end",
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setPending('corroborate'),
                                children: "Corroborate…"
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setPending('contradict'),
                                children: "Mark contradicted…"
                            }),
                            claim.viewerIsAuthor && onChangeAudience && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: onChangeAudience,
                                children: "Change who can see this…"
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setPending('history'),
                                children: "Audience history"
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setPending('retract'),
                                "data-testid": Testids.registryHearsay.retract,
                                children: "Retract…"
                            })
                        ]
                    })
                ]
            }),
            form && /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
                open: true,
                onOpenChange: (o)=>!o && close(),
                size: "sm",
                title: form.title,
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: form.label,
                        htmlFor: noteId,
                        required: pending === 'retract',
                        error: error || undefined,
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: noteId,
                            value: note,
                            maxLength: 2000,
                            onChange: (e)=>{
                                setNote(e.target.value);
                                setError('');
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                        confirmLabel: form.confirm,
                        onConfirm: confirm,
                        onCancel: close,
                        loading: verify.isPending || retract.isPending,
                        confirmProps: pending === 'retract' ? {
                            variant: 'destructive',
                            'data-testid': Testids.registryHearsay.retractConfirm
                        } : undefined
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(AudienceHistoryDialog, {
                claimId: claim.id,
                open: pending === 'history',
                onOpenChange: (o)=>!o && close()
            })
        ]
    });
}
